import { AUTH_BASE_URL } from '../config/api';

const API_BASE_URL = AUTH_BASE_URL;

// Get all available tools for a provider
export const fetchToolsForProvider = async (provider) => {
  const response = await fetch(`${API_BASE_URL}/api/tools/provider/${provider}`, {
    credentials: 'include',
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch tools for provider');
  }
  
  return response.json();
};

// Get tools supported by a specific model
export const fetchModelTools = async (provider, model) => {
  const response = await fetch(`${API_BASE_URL}/api/tools/model/${provider}/${model}`, {
    credentials: 'include',
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch model tools');
  }
  
  return response.json();
};

// Auto-detect tools from a query
export const detectToolsFromQuery = async (provider, query) => {
  const response = await fetch(`${API_BASE_URL}/api/tools/detect/${provider}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ query }),
  });
  
  if (!response.ok) {
    throw new Error('Failed to detect tools');
  }
  
  return response.json();
};

// Get all tool definitions
export const fetchToolDefinitions = async () => {
  const response = await fetch(`${API_BASE_URL}/api/tools/definitions`, {
    credentials: 'include',
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch tool definitions');
  }
  
  return response.json();
};

// Get pricing for tools
export const fetchToolPricing = async (provider) => {
  const response = await fetch(`${API_BASE_URL}/api/tools/pricing/${provider}`, {
    credentials: 'include',
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch tool pricing');
  }
  
  return response.json();
};

// Enhanced tool configuration with icons and colors
export const ENHANCED_TOOL_CONFIG = {
  web_search: {
    icon: 'Globe',
    bgColor: 'bg-blue-500',
    textColor: 'text-white',
    category: 'information',
    description: 'Search the web for real-time information'
  },
  code_execution: {
    icon: 'Code',
    bgColor: 'bg-green-500',
    textColor: 'text-white',
    category: 'computation',
    description: 'Execute Python code and data analysis'
  },
  image_generation: {
    icon: 'Palette',
    bgColor: 'bg-purple-500',
    textColor: 'text-white',
    category: 'creative',
    description: 'Generate and edit images'
  },
  computer_use: {
    icon: 'Monitor',
    bgColor: 'bg-gray-700',
    textColor: 'text-white',
    category: 'automation',
    description: 'Interact with computer interfaces'
  },
  file_search: {
    icon: 'FileText',
    bgColor: 'bg-orange-500',
    textColor: 'text-white',
    category: 'information',
    description: 'Search through uploaded files'
  },
  function_calling: {
    icon: 'Zap',
    bgColor: 'bg-yellow-500',
    textColor: 'text-white',
    category: 'integration',
    description: 'Call custom functions and APIs'
  },
  math_solver: {
    icon: 'Calculator',
    bgColor: 'bg-indigo-500',
    textColor: 'text-white',
    category: 'computation',
    description: 'Solve complex mathematical problems'
  }
};

// Get provider from model
export const getProviderFromModel = (model) => {
  if (model.includes('gpt') || model.includes('o1') || model.includes('o3')) {
    return 'openai';
  } else if (model.includes('claude')) {
    return 'anthropic';
  } else if (model.includes('gemini')) {
    return 'google';
  }
  return 'openai'; // default
};

// Transform tools for UI display
export const transformToolsForUI = (tools) => {
  return tools.map(tool => ({
    ...tool,
    ...ENHANCED_TOOL_CONFIG[tool.id],
    displayName: tool.name || tool.id.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
  }));
};

// Group tools by category
export const groupToolsByCategory = (tools) => {
  const groups = {
    information: [],
    computation: [],
    creative: [],
    automation: [],
    integration: []
  };
  
  tools.forEach(tool => {
    const config = ENHANCED_TOOL_CONFIG[tool.id];
    const category = config?.category || 'integration';
    if (groups[category]) {
      groups[category].push({
        ...tool,
        ...config
      });
    }
  });
  
  return groups;
};

// Legacy tool mapping for backward compatibility
export const LEGACY_TOOL_MAPPING = {
  'image_generation': 'image_generation',
  'code_interpreter': 'code_execution',
  'file_search': 'file_search'
};

// Convert legacy tool IDs to new system
export const convertLegacyTools = (legacyTools) => {
  return legacyTools.map(toolId => LEGACY_TOOL_MAPPING[toolId] || toolId);
};